import type { PageHelp } from "../../help/types";

export const hrNeedsAttentionPageHelp: PageHelp = {
  title: "Needs attention",
  intro:
    "This list collects everything in your client companies that is waiting for an HR or GrECo decision: profile changes and documents from the employee portal, new or changed dependents, enrollments and cancellations waiting for approval, and enrollments that break a package's age limits. Open it with the bell button on the HR page and work through the rows. Each item leaves the list once it is handled.",
  sections: [
    "Getting around",
    "Scope and refresh",
    "The list",
    "Row actions",
    "Enrollments and dependents",
  ],
  contexts: {
    "row-expanded":
      "Click an employee row (or the arrow at the right of the name) to expand it. Only one row is open at a time.",
  },
  tips: [
    "An item leaves this list when it is handled: HR or GrECo saves or confirms the employee's or dependent's data, or approves or rejects the pending enrollment or cancellation. An age-limit warning stays for as long as the person is outside the package's age limits.",
    "You only see companies you have access to. Items from benefits where your access is set to no permission are left out.",
    "Approving, rejecting and confirming reload the list automatically. Use the refresh button only to pick up changes made elsewhere in the meantime.",
  ],
  steps: [
    {
      selector: '[data-help="needs-attention-back"]',
      section: "Getting around",
      title: "Go back to the HR page",
      body: "The HR breadcrumb next to the bell button takes you back to the HR page. It is shown only while this list is open.",
    },
    {
      selector: '[data-help="needs-attention-toggle"]',
      section: "Getting around",
      title: "Close the list with the bell button",
      body: "The bell button stays highlighted while this list is open. Click it to go back to the page you came from. The red number on it counts open items, not employees: each profile change, dependent, pending approval and age-limit warning counts separately.",
    },
    {
      selector: '[data-help="subheader-client-select"]',
      section: "Scope and refresh",
      title: "Narrow the list to one company",
      body: "Pick a company to see only that company's items. Clear the field to see items from every client company you have access to. The choice is shared with the HR page, so the company is still selected when you go back.",
    },
    {
      selector: '[data-help="subheader-refresh"]',
      section: "Scope and refresh",
      title: "Reload the list",
      body: "Fetches the open items from the server again, for example after an employee has just changed their profile in the employee portal.",
    },
    {
      selector: '[data-help="hr-needs-attention-column-name"]',
      section: "The list",
      title: "See who needs attention",
      body: "Each row is one employee with at least one open item, either for the employee or for one of their dependents. A dependent's item is listed under the employee it belongs to.",
    },
    {
      selector: '[data-help="hr-needs-attention-column-company"]',
      section: "The list",
      title: "Check which company a row belongs to",
      body: "When no company is selected, the list mixes employees from all your client companies. This column tells them apart.",
    },
    {
      selector: '[data-help="hr-employee-name"]',
      section: "The list",
      optional: true,
      title: "Expand a row to see the details",
      body: "Click a row to open it. The expanded part shows the enrollments that need attention and, under Dependents, the dependents with open items. Click the row again to close it.",
    },
    {
      selector: '[data-help="hr-employee-edit"]',
      section: "Row actions",
      optional: true,
      title: "Review and confirm profile changes",
      body: "The edit icon turns yellow when the employee changed profile data or uploaded a document in the employee portal. On this page the icon is enabled only for those rows. It opens the employee form with the changed fields highlighted in yellow: click Confirm to accept them, or correct the data and click Save. Read-only users see the icon disabled.",
    },
    {
      selector: '[data-help="hr-employee-export-consents"]',
      section: "Row actions",
      optional: true,
      title: "Export one employee's consents",
      body: "Opens Export Consents for this employee. Tick Is Salary Deduction Consent to export the salary-deduction consents instead of the regular consents. After you click Export, you get an email with a download link. The export runs for the company selected at the top, so select the employee's company first.",
    },
    {
      selector: '[data-help="hr-enrollment-row"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Read an enrollment line",
      body: "Each line shows the benefit category, benefit and package, the package's Valid From/To dates, Eligible From and, when set, Enrollment Date and Cancelation Date. A dashed green border means an enrollment is waiting for approval. A dashed red border means a cancellation is waiting for approval.",
    },
    {
      selector: '[data-help="hr-enrollment-approve"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Approve an enrollment or cancellation",
      body: "The green button approves whatever is pending on this line. Its tooltip says Approve Enrollment or Approve Cancellation. For an enrollment, you confirm a Date of Approval (the requested enrollment date is filled in), and that date becomes the enrollment date. This needs edit rights: with a read-only role the change is refused.",
    },
    {
      selector: '[data-help="hr-enrollment-reject"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Reject an enrollment or cancellation",
      body: "The red button rejects the pending request and asks for a Rejection Comment. Rejecting an enrollment removes its enrollment date, and if the enrollment was a package upgrade, the previous package is restored. Rejecting a cancellation removes the cancellation date, so the enrollment continues.",
    },
    {
      selector: '[data-help="hr-enrollment-consent-documents"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Check the consent documents before deciding",
      body: "Next to the approve and reject buttons, the document button opens the consent documents attached to this enrollment, read-only. Its tooltip shows how many documents there are.",
    },
    {
      selector: '[data-help="hr-enrollment-age-warning"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Understand an age-limit warning",
      body: "A red calendar icon means the person's age is outside the package's age limits. Hover over it to see the age and the limits. The line stays on this list for as long as the person is outside the limits.",
    },
    {
      selector: '[data-help="hr-enrollment-edit"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Edit the enrollment",
      body: "Opens Edit Enrollment for this line, for example to correct its dates. On this page the button is shown only on lines with an age-limit warning or a rejection comment. Read-only users see it disabled.",
    },
    {
      selector: '[data-help="hr-dependent-row"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Find the dependents with open items",
      body: "Dependents with open items are listed under Dependents, with their enrollment lines indented below them. A red warning icon marks a dependent who needs attention.",
    },
    {
      selector: '[data-help="hr-dependent-edit"]',
      section: "Enrollments and dependents",
      context: "row-expanded",
      optional: true,
      title: "Confirm a new or changed dependent",
      body: "The edit icon is green for a dependent the employee added in the employee portal, and yellow for a dependent whose data the employee changed. It opens the dependent's form with the changed fields highlighted. Save the form to clear the item. Read-only users see the icon disabled.",
    },
  ],
};
